import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft, RefreshCw, CheckCircle } from 'lucide-react';
import { apiClient } from '../../services/api/apiClient';
import toast from '../../utils/toast';
import { getImageUrl } from '../../utils/imageUtils';

interface ForgotPasswordFormTWProps {
  onBackToLogin?: () => void;
}

interface ForgotPasswordData {
  email: string;
}

export const ForgotPasswordFormTW: React.FC<ForgotPasswordFormTWProps> = ({
  onBackToLogin,
}) => {
  const [formData, setFormData] = useState<ForgotPasswordData>({
    email: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'E-Mail-Adresse ist erforderlich';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setFormErrors({});
    setShowResendButton(false);

    try {
      const response = await apiClient.forgotPassword(formData.email);

      if (response.success) {
        setIsSubmitted(true);
        toast.success(response.message || 'Passwort-Reset E-Mail wurde gesendet');
      } else {
        if (response.data?.action === 'resend_verification') {
          setShowResendButton(true);
          setFormErrors({
            general: response.error + ' Sie können die E-Mail-Bestätigung erneut anfordern.'
          });
        } else {
          setFormErrors({ general: response.error || 'Fehler beim Senden der E-Mail' });
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setFormErrors({ general: 'Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;

    setResendLoading(true);
    try {
      const result = await apiClient.resendVerification(formData.email);

      if (result.success) {
        toast.success(result.data?.message || 'Bestätigungs-E-Mail wurde erneut gesendet!');
        setShowResendButton(false);
        setFormErrors({});
      } else {
        toast.error(result.error || 'Fehler beim Senden der E-Mail');
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast.error('Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isEmailValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(formData.email.trim());
  };

  // Logo-URL - zentrale Funktion für korrekte Umgebungserkennung
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  // Success State
  if (isSubmitted) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm space-y-10">
          {/* Logo */}
          <div>
            <Link to="/" aria-label="Zur Startseite" className="block">
              <img
                alt="Renditefuchs"
                src={logoUrl}
                className="mx-auto h-10 w-auto"
              />
            </Link>
            <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
              E-Mail gesendet
            </h2>
          </div>

          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-status-success/10 border border-green-500/50">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-status-success shrink-0 mt-0.5" />
                <p className="text-sm text-green-400">
                  Wir haben Ihnen eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts an{' '}
                  <strong className="text-green-300">{formData.email}</strong> gesendet.
                </p>
              </div>
            </div>

            {onBackToLogin && (
              <button
                type="button"
                onClick={onBackToLogin}
                className="flex w-full justify-center items-center gap-2 rounded-md bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm/6 font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück zur Anmeldung
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-10">
        {/* Logo & Header */}
        <div>
          <Link to="/" aria-label="Zur Startseite" className="block">
            <img
              alt="Renditefuchs"
              src={logoUrl}
              className="mx-auto h-10 w-auto"
            />
          </Link>
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            Passwort zurücksetzen
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on" noValidate>
          {/* Error Message */}
          {formErrors.general && (
            <div className="p-4 rounded-lg bg-status-error/10 border border-red-500/50">
              <p className="text-sm text-red-400">{formErrors.general}</p>
              {showResendButton && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="flex items-center gap-2 text-sm font-medium text-brand hover:text-brand-hover transition-colors disabled:opacity-50"
                  >
                    {resendLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        E-Mail erneut senden
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Email Field */}
          <div>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Mail className="h-5 w-5 text-gray-500" aria-hidden="true" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="ihre@email.de"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={isLoading}
                className={`block w-full rounded-md bg-white dark:bg-white/5 pl-10 pr-3 py-2 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-brand sm:text-sm/6 ${
                  formErrors.email
                    ? 'outline-red-500 focus:outline-red-500'
                    : 'outline-gray-300 dark:outline-white/10'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              />
            </div>
            {formErrors.email && (
              <p className="mt-2 text-sm text-red-400">{formErrors.email}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !isEmailValid()}
            className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <span>Passwort zurücksetzen</span>
            )}
          </button>

          {/* Back to Login */}
          {onBackToLogin && (
            <p className="mt-10 text-center text-sm/6 text-gray-500 dark:text-gray-400">
              <button
                type="button"
                onClick={onBackToLogin}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 w-full text-brand hover:text-brand-hover transition-colors disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück zur Anmeldung
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};
